import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivityTypeEnum, RangeQueryInterface } from '@sport-activities/sdk';
import { Subscription, of } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';

import { appConfig } from '~/app.config';
import { PaginatedSearchService } from '~/attendee/service/paginated-search.service';
import { SubscriptionSearchService } from '~/attendee/service/subscription-search.service';
import { buildRangeQuery } from '~/cms/helpers/build-range-query.helper';
import { Parameters } from '~/cms/interfaces/dynamic-page.cms.interface';
import { ActivitySectionCms } from '~/cms/interfaces/homepage.cms.interface';

import { Activity } from '~/core/model/activity.model';
import { MapsService } from '~/core/services/maps/maps.service';

import { environment } from 'src/environments/environment';
import { ActivityFilterParamsInterface } from '~/attendee/interfaces/activity-filter-params.interface';

interface GeoFilter {
  distance: number;
  latitude: number;
  longitude: number;
}

@Component({
  selector: 'app-cms-activities-section',
  templateUrl: './activities-section.component.html',
})
export class CmsActivitiesSectionComponent implements OnInit, OnDestroy {
  @Input() public section: ActivitySectionCms;
  private readonly subscriptions = new Subscription();
  public activities: Activity[];
  public city: string;

  constructor(
    private readonly subscriptionSearchService: SubscriptionSearchService,
    @Inject('ActivitySearchService')
    private readonly searchService: PaginatedSearchService<
      Activity,
      ActivityFilterParamsInterface
    >,
    private readonly mapsService: MapsService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.mapsService.initMapScript();
    this.subscriptions.add(
      this.mapsService
        .mapScriptInitialized()
        .pipe(
          filter((woosmapLoaded) => woosmapLoaded === true),
          switchMap(() => {
            if (!this.section?.public_id) {
              return of(null);
            }
            return this.mapsService.getPlace(this.section?.public_id);
          }),
          switchMap((place) => {
            this.city = place?.name;

            const types =
              this.section.is_live === true
                ? ['LIVE' as ActivityTypeEnum]
                : [
                    'GROUP_LESSON' as ActivityTypeEnum,
                    'GROUP_OUTING' as ActivityTypeEnum,
                    'WORKSHOP' as ActivityTypeEnum,
                    'MULTI_DAY_COURSE' as ActivityTypeEnum,
                    'EVENT' as ActivityTypeEnum,
                    'PLAYGROUND_RENT' as ActivityTypeEnum,
                    'BIRTHDAY' as ActivityTypeEnum,
                  ];

            const geoFilter = place ? this.getGeoFilter(place) : undefined;

            const filters =
              this.subscriptionSearchService.getDynamicPageActivitiesFilters(
                this.section,
                types,
                environment.tenant,
                this.ageRange,
                this.priceRange,
                geoFilter
              );

            return this.searchService
              .search({
                ...filters,
                pagination: {
                  from: 0,
                  limit: this.section.number_to_display,
                },
                includeNextDates: true,
              })
              .pipe(
                first(),
                map((res) => res.items)
              );
          })
        )
        .subscribe((activities) => {
          this.activities = activities;
        })
    );
  }

  private get ageRange(): RangeQueryInterface<number> {
    return buildRangeQuery(this.section.age_range);
  }

  private get priceRange(): RangeQueryInterface<number> {
    return buildRangeQuery(this.section.price_range);
  }

  public get parameters(): Parameters {
    let parameters = {};

    if (this.section.sport_group) {
      parameters = {
        ...parameters,
        sportGroups: this.section.sport_group,
      };
    }

    if (this.ageRange) {
      parameters = {
        ...parameters,
        'ageRange[gte]': this.ageRange?.gte,
        'ageRange[lte]': this.ageRange?.lte,
      };
    }

    if (this.priceRange) {
      parameters = {
        ...parameters,
        'priceRange[gte]': this.priceRange?.gte,
        'priceRange[lte]': this.priceRange?.lte,
      };
    }

    if (this.section.date_range?.from) {
      parameters = {
        ...parameters,
        startDate: this.section.date_range.from,
      };
    }

    if (this.section.date_range?.to) {
      parameters = {
        ...parameters,
        endDate: this.section.date_range.to,
      };
    }

    if (this.city) {
      parameters = {
        ...parameters,
        city: this.city,
        placeId: this.section.public_id,
      };
    }

    return parameters;
  }

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  private getGeoFilter(place: any): GeoFilter | undefined {
    const latitude = +place?.geometry?.location?.lat;
    const longitude = +place?.geometry?.location?.lng;

    return latitude && longitude
      ? {
          latitude,
          longitude,
          distance: +appConfig.filters.defaultDistance,
        }
      : undefined;
  }
}
